import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Navigation, Crosshair, Layers } from "lucide-react";

const DARK_TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const SATELLITE_TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

function distanceMeters(a, b) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export const MapView = ({ center, monuments, onSelect, activeId, speak, onApproach }) => {
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const markersRef = useRef({});
  const gpsMarkerRef = useRef(null);
  const watchRef = useRef(null);
  const announcedRef = useRef({});
  const insideRef = useRef(new Set());
  const tileLayersRef = useRef({});
  const [satelliteOn, setSatelliteOn] = useState(false);
  const [gpsOn, setGpsOn] = useState(false);
  const [userPos, setUserPos] = useState(null);
  const COOLDOWN = 90000; // no repetir el mismo aviso en 90s

  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
        const map = L.map(mapEl.current, {
      center: [center.lat, center.lng],
      zoom: 16,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;
    const streetLayer = L.tileLayer(DARK_TILES, { maxZoom: 19, subdomains: "abc", attribution: "© OpenStreetMap contributors" });
const satelliteLayer = L.tileLayer(SATELLITE_TILES, { maxZoom: 19, attribution: "© Esri" });
streetLayer.addTo(map);
tileLayersRef.current = { streetLayer, satelliteLayer };

       monuments.forEach((m) => {
      const icon = L.divIcon({
        className: "",
                                                html: `<div class="mon-marker" data-id="${m.id}">
          ${m.id === "m-plaza-mayor" ? '<div class="mon-free-badge">FREE</div>' : ""}
          <div class="mon-marker-shadow"></div>
          <img src="/pin-marker.svg" style="width: 26px; height: 46px; display: block;" />
        </div>`,
        iconSize: [26, 46],
        iconAnchor: [13, 46],
      });
      const marker = L.marker([m.lat, m.lng], { icon }).addTo(map);
      marker.on("click", () => onSelect(m));
      markersRef.current[m.id] = marker;
    });

    const bounds = L.latLngBounds(monuments.map((m) => [m.lat, m.lng]));
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });

    const invalidateTimer = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 200);

    return () => {
      clearTimeout(invalidateTimer);
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Highlight active marker
    useEffect(() => {
    if (!mapRef.current) return;
    Object.values(markersRef.current).forEach((marker) => mapRef.current.removeLayer(marker));
    markersRef.current = {};
    monuments.forEach((m) => {
      const icon = L.divIcon({
        className: "",
        html: `<div class="mon-marker" data-id="${m.id}">
          ${m.id === "m-plaza-mayor" ? '<div class="mon-free-badge">FREE</div>' : ""}
          <div class="mon-marker-shadow"></div>
          <img src="/pin-marker.svg" style="width: 26px; height: 46px; display: block;" />
        </div>`,
        iconSize: [26, 46],
        iconAnchor: [13, 46],
      });
      const marker = L.marker([m.lat, m.lng], { icon }).addTo(mapRef.current);
      marker.on("click", () => onSelect(m));
      markersRef.current[m.id] = marker;
    });
  }, [monuments]);

  const checkProximity = (p) => {
    const now = Date.now();
    monuments.forEach((m) => {
      const d = distanceMeters(p, m);
      const radius = m.radius || 40;
      if (d <= radius) {
        if (!insideRef.current.has(m.id)) {
          insideRef.current.add(m.id);
          const last = announcedRef.current[m.id] || 0;
          if (now - last > COOLDOWN) {
            announcedRef.current[m.id] = now;
            const greeting = `Os acercáis a ${m.title}. ${m.short} Pulsad para escuchar su historia.`;
            if (speak) speak(greeting);
            if (onApproach) onApproach(m);
          }
        }
      } else if (d > radius * 1.4) {
        insideRef.current.delete(m.id);
      }
    });
  };
  const toggleSatellite = () => {
    const { streetLayer, satelliteLayer } = tileLayersRef.current;
    if (!streetLayer || !satelliteLayer || !mapRef.current) return;
    if (satelliteOn) {
      mapRef.current.removeLayer(satelliteLayer);
      streetLayer.addTo(mapRef.current);
    } else {
      mapRef.current.removeLayer(streetLayer);
      satelliteLayer.addTo(mapRef.current);
    }
    setSatelliteOn(!satelliteOn);
  };
  const toggleGps = () => {
    if (gpsOn) {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      if (gpsMarkerRef.current) {
        mapRef.current.removeLayer(gpsMarkerRef.current);
        gpsMarkerRef.current = null;
      }
      setGpsOn(false);
      setUserPos(null);
      return;
    }
    if (!navigator.geolocation) return;
    setGpsOn(true);
    let centered = false;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserPos(p);
        checkProximity(p);
        const icon = L.divIcon({ className: "", html: '<div class="gps-dot"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });
        if (!gpsMarkerRef.current) {
          gpsMarkerRef.current = L.marker([p.lat, p.lng], { icon }).addTo(mapRef.current);
        } else {
          gpsMarkerRef.current.setLatLng([p.lat, p.lng]);
        }
        if (!centered) {
          centered = true;
          mapRef.current.setView([p.lat, p.lng], 17, { animate: true });
        }
      },
            () => setGpsOn(false),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
  };

  const recenter = () => {
    if (userPos) mapRef.current.setView([userPos.lat, userPos.lng], 17, { animate: true });
    else mapRef.current.setView([center.lat, center.lng], 16, { animate: true });
  };

  // nearest monument label
  const nearest =
    userPos && monuments.length
      ? monuments
          .map((m) => ({ m, d: distanceMeters(userPos, m) }))
          .sort((a, b) => a.d - b.d)[0]
      : null;

  return (
    <div className="relative w-full h-screen">
      <div ref={mapEl} className="absolute inset-0" data-testid="leaflet-map" />

  

      {nearest && nearest.d < 400 && (
        <div className="absolute top-28 left-1/2 -translate-x-1/2 z-[500]">
          <button
            data-testid="nearest-monument-chip"
            onClick={() => onSelect(nearest.m)}
            className="glass rounded-full px-4 py-2 flex items-center gap-2 gold-glow"
          >
            <Navigation size={14} className="gold-text" />
            <span className="text-xs" style={{ color: "#f3f4f6" }}>
              {nearest.m.title} · {Math.round(nearest.d)} m
            </span>
          </button>
        </div>
      )}

      <div className="absolute right-4 bottom-40 z-[500] flex flex-col gap-3">
        <button
          data-testid="gps-toggle-btn"
          onClick={toggleGps}
          className={`w-12 h-12 rounded-full glass flex items-center justify-center transition-all duration-300 ${gpsOn ? "gold-glow" : ""}`}
          style={gpsOn ? { borderColor: "rgba(245,158,11,0.5)" } : {}}
        >
          <Navigation size={20} style={{ color: gpsOn ? "#f0c94a" : "#9ca3af" }} />
        </button>
        <button
          data-testid="recenter-btn"
          onClick={recenter}
          className="w-12 h-12 rounded-full glass flex items-center justify-center"
        >
          <Crosshair size={20} style={{ color: "#9ca3af" }} />
        </button>
        <button
          data-testid="satellite-toggle-btn"
          onClick={toggleSatellite}
          className={`w-12 h-12 rounded-full glass flex items-center justify-center transition-all duration-300 ${satelliteOn ? "gold-glow" : ""}`}
          style={satelliteOn ? { borderColor: "rgba(245,158,11,0.5)" } : {}}
        >
          <Layers size={20} style={{ color: satelliteOn ? "#f0c94a" : "#9ca3af" }} />
        </button>
      </div>
    </div>
  );
};
